import DateTimeHandlerService from "@/services/common/date-time/DateTimeHandlerService";
import DateTimeHandlerServiceV2 from "@/services/common/date-time/DateTimeHandlerServiceV2";
import ObjectHandleService from "@/services/common/ObjectHandleService";
import FileSaver from "file-saver";
import i18n from "@/i18n";
import {
	CreateEmptyConfig,
	GearboxFactory, GearboxStagesFactory,
	 GearsFactory, GearsMeshFrequenciesFactory, TurbinePropertiesFactory, TurbineShaftsFactory, TurbineVersionFactory
} from "../factories/KinematicParametersFactory";

/**
 * Regras de validação de textfields
 */
export const textfieldsRules =
{
	required: v => (v !== null && v !== undefined && v !== "") || i18n.t("ValidationMessages.RequiredField"),
	positive: v => Number(v) > 0 || i18n.t("ValidationMessages.OnlyPositiveNumbers"),
	integer:  v => Number.isInteger(Number(v)) || i18n.t("ValidationMessages.OnlyIntegerNumbers"),
};

/**
 * Regras de validação de arquivos
 */
export const fileRules =
{
	required: v => !!v || i18n.t("ValidationMessages.RequiredFile"),
	isJson:   v => !v || v === null || (v instanceof File && v.type === "application/json") ||
		i18n.t("ValidationMessages.MustBeJsonFile"),
};

/**
 * Lê o arquivo JSON e retorna o objeto/array
 */
const ReadJsonFile = (file) =>
{
	return new Promise((resolve, reject) =>
	{
		const reader = new FileReader();
		reader.onload = event =>
		{
			try
			{
				const json = JSON.parse(event.target.result);
				resolve(json);
			}
			catch (error)
			{
				reject(error);
			}
		};
		reader.onerror = error => reject(error);
		reader.readAsText(file);
	});
};

export const BuildKinematicConfiguration = async(configuration, toDownload) =>
{
	const configurationClone = ObjectHandleService.Clone(configuration);
	const turbineProperties = configurationClone.turbineProperties;
  
	if (configuration.turbineProperties.sCurve instanceof File)
	{
		const extractedData = await ReadJsonFile(configuration.turbineProperties.sCurve);
          
		if (Array.isArray(extractedData))
		{
			turbineProperties.sCurveDouble = extractedData;
		}
	}
	else
	{
		turbineProperties.sCurveDouble = [];
	}
  
	delete turbineProperties.sCurve;

	const turbineVersion = configurationClone.turbineVersionsModel;

	turbineVersion.effectiveSince = toDownload ? turbineVersion.newEffectiveSince :
		DateTimeHandlerService.ToUTCDateTime(turbineVersion.newEffectiveSince);
	delete turbineVersion.newEffectiveSince;
	turbineVersion.createdAt = DateTimeHandlerServiceV2.GetCurrentUTCDateTimeISO();

	const configurationToCreate = {
		turbineProperties: {
			...turbineProperties,
			turbineVersion: {
				...turbineVersion,
				gearboxModel: {
					...configurationClone.gearbox,
					gearboxStages: configurationClone.gearboxStages,
				},
				shafts: configurationClone.shafts
			}
		},
		gearsMeshFrequencies: configurationClone.gearsMeshFrequencies,
		gears:                configurationClone.gears
	};

	return configurationToCreate;
};

export const NormalizeConfiguration = (configuration) =>
{
	const pascalCaseConfiguration = TransformKeysToCamelOrPascal(configuration);

	if (ObjectHandleService.IsEmpty(pascalCaseConfiguration)) return CreateEmptyConfig();
	const turbinesProperties = pascalCaseConfiguration.turbineProperties;

	const sCurveArray = turbinesProperties.sCurveDouble || turbinesProperties.sCurve || [];

	if (Array.isArray(sCurveArray) && sCurveArray.length > 0)
	{
		const jsonString = JSON.stringify(sCurveArray);
		const blob = new Blob([ jsonString ], { type: "application/json" });
		turbinesProperties.sCurve = new File([ blob ], "SCurve.json", { type: "application/json" });
	}
	else
	{
		turbinesProperties.sCurve = null;
	}

	const { shafts, gearboxModel } = pascalCaseConfiguration.turbineProperties.turbineVersion;
	const turbineProperties = TurbinePropertiesFactory(pascalCaseConfiguration.turbineProperties);
	const turbineVersion = TurbineVersionFactory(pascalCaseConfiguration.turbineProperties.turbineVersion);
	const shaftsModel = shafts?.map(shaft => TurbineShaftsFactory(shaft)) || [];
	const gearbox = GearboxFactory(gearboxModel);
	const gearboxStages = gearboxModel?.gearboxStages?.map(stage => GearboxStagesFactory(stage)) || [];
	const gearsMeshFrequencies = pascalCaseConfiguration.gearsMeshFrequencies
    ?.map(gearsMeshFrequencies => GearsMeshFrequenciesFactory(gearsMeshFrequencies)) || [];
	const gears = pascalCaseConfiguration.gears?.map(gears => GearsFactory(gears)) || [];

	return {
		turbineProperties,
		turbineVersion,
		shaftsModel,
		gearbox,
		gearboxStages,
		gearsMeshFrequencies,
		gears
	};
};

/**
 * Transforma as chaves de um objeto (ou array de objetos) convertendo-as para camelCase
 * ou PascalCase, com exceção
 * de algumas chaves específicas que são forçadas para minúsculas.
 *
 * As chaves forçadas para minúsculas são: "BPFI", "BPFO", "BSF", "FTF".
 *
 * A função é recursiva e aplica a normalização em objetos aninhados e arrays.
 *
 * @param {Object|Array} obj - Objeto ou array de objetos que terá as chaves normalizadas.
 * @param {boolean} [toCamelCase=true] - Se `true`, converte para camelCase.
 * Se `false`, converte para PascalCase.
 * @returns {Object|Array} Um novo objeto (ou array de objetos) com as chaves normalizadas.
 */
const TransformKeysToCamelOrPascal = (obj, toCamelCase = true) =>
{
	const FORCE_LOWERCASE = [ "BPFI", "BPFO", "BSF", "FTF" ];

	// Se for um array, aplica a normalização em cada item recursivamente
	if (Array.isArray(obj))
	{
		return obj.map((item) => TransformKeysToCamelOrPascal(item, toCamelCase));
	}

	// Se for um objeto não-nulo
	if (ObjectHandleService.IsObject(obj))
	{
		const result = {};

		// Itera sobre todas as chaves do objeto
		for (const key in obj)
		{
			// Garante que a chave pertence ao objeto e não ao protótipo
			if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

			let newKey;

			// Se a chave estiver na lista de forçadas, converte para minúsculo
			if (FORCE_LOWERCASE.includes(key))
			{
				newKey = key.toLowerCase();
			}
			else
			{
				// Converte a primeira letra da chave com base no parâmetro `toCamelCase`
				newKey = toCamelCase ?
					key.charAt(0).toLowerCase() + key.slice(1) :
					key.charAt(0).toUpperCase() + key.slice(1);
			}
			// Aplica a função recursivamente ao valor associado à chave
			result[newKey] = TransformKeysToCamelOrPascal(obj[key], toCamelCase);
		}
		return result;
	}

	return obj;
};

export const DownloadFile = (fileToSave, fileName = "kinematic-configuration.json") =>
{
	FileSaver.saveAs(fileToSave, fileName);
};